import React from 'react';
import { useTranslation } from 'react-i18next';
import { H3, Paragraph, Section } from './ReceiptTypography';
import { InnerPrinterReceiptElementID } from '../../providers/PrintProvider';
import { useSelector } from 'react-redux';
import usePaperWidth from '../../hooks/usePaperWidth';
import { useHelpers } from '../../providers/HelpersProvider';
import classnames from 'classnames';

export default function InnerPrinterOrderHistoryReceipt(props) {
  const { t: translate } = useTranslation(null, { keyPrefix: 'Components:Print:InnerPrinterOrderHistoryReceipt' });

  const { formatCurrency, formatDateTime } = useHelpers();

  const { payload } = props;
  const { selectedFilter, stats } = payload;
  const { company } = useSelector((state) => state.company);
  const lng = company?.legal_lang ?? company?.region?.default_locale;

  const { isWidePaper, pageWidth } = usePaperWidth();

  function t(key) {
    return translate(key, { lng });
  }

  const renderDateRange = () => {
    if (!selectedFilter?.start_date) {
      return null;
    }

    return `${formatDateTime(selectedFilter.start_date, { format: 'dd.MM.yyyy' }, lng)} - ${formatDateTime(selectedFilter.end_date, { format: 'DD.MM.YYYY' }, lng)}`;
  };

  const renderTotalOrders = () => (
    <Section>
      <H3>
        {t('total_orders.title')} {renderDateRange()}
      </H3>
      <Paragraph wide={isWidePaper}>
        {t('total_orders.orders')} {stats?.orders_count ?? 0}
      </Paragraph>
      <Paragraph wide={isWidePaper}>
        {t('total_orders.sales_volume')} {formatCurrency(stats?.sales ?? 0)}
      </Paragraph>
      <Paragraph wide={isWidePaper}>
        {t('total_orders.delivery_costs')} {formatCurrency(stats?.delivery_fees ?? 0)}
      </Paragraph>
    </Section>
  );

  const renderCashOrders = () => (
    <Section>
      <H3>{t('cash_orders.title')}</H3>
      <Paragraph wide={isWidePaper}>
        {t('cash_orders.orders')} {stats?.cash_orders_count ?? 0}
      </Paragraph>
      <Paragraph wide={isWidePaper}>
        {t('cash_orders.sales_volume')} {formatCurrency(stats?.cash_sales ?? 0)}
      </Paragraph>
      <Paragraph wide={isWidePaper}>
        {t('cash_orders.delivery_costs')} {formatCurrency(stats?.cash_delivery_fees ?? 0)}
      </Paragraph>
    </Section>
  );

  const renderOnlineOrders = () => (
    <Section>
      <H3>{t('online_orders.title')}</H3>
      <Paragraph wide={isWidePaper}>
        {t('online_orders.orders')} {stats?.online_orders_count ?? 0}
      </Paragraph>
      <Paragraph wide={isWidePaper}>
        {t('online_orders.sales_volume')} {formatCurrency(stats?.online_sales ?? 0)}
      </Paragraph>
      <Paragraph wide={isWidePaper}>
        {t('online_orders.delivery_costs')} {formatCurrency(stats?.online_delivery_fees ?? 0)}
      </Paragraph>
    </Section>
  );

  return (
    <div
      className="bottom-0 box-border fixed pt-[50px] pb-[130px]"
      id={InnerPrinterReceiptElementID}
      style={{
        zIndex: 0,
        width: `${pageWidth}px`,
        minWidth: `${pageWidth}px`,
        maxWidth: `${pageWidth}px`,
        left: `-${pageWidth}px`,
      }}
    >
      <Paragraph wide={isWidePaper}>{t('total')}</Paragraph>
      <Paragraph wide={isWidePaper}>{company?.name}</Paragraph>
      {renderTotalOrders()}
      {renderCashOrders()}
      {renderOnlineOrders()}
      <CollectedTaxesList payload={stats?.online_collected_taxes_list} type={PAYMENT_TYPE.Online} wide={isWidePaper} />
      <CollectedTaxesList payload={stats?.cash_collected_taxes_list} type={PAYMENT_TYPE.Cash} wide={isWidePaper} />
    </div>
  );
}

const PAYMENT_TYPE = Object.freeze({
  Cash: 'cash',
  Online: 'online',
});

function CollectedTaxesList({ payload, type, wide }) {
  const { t } = useTranslation(null, {
    keyPrefix: 'Components:Print:InnerPrinterOrderHistoryReceipt.collected_taxes_list',
  });
  const { formatCurrency } = useHelpers();
  const companyLng = useSelector(
    (state) => state.company.company?.legal_lang ?? state.company.company?.region?.default_locale,
  );
  const translator = (key, params) => t(key, { lng: companyLng, ...params });

  if (!payload?.length) {
    return null;
  }

  return (
    <>
      <H3>{translator(`title.${type}`)}</H3>
      {/* Dense 5-column table, so it stays subordinate like Paragraph's `sub` rather than
          stepping all the way up to the regular 24px — at 24px the columns stop fitting 576 dots. */}
      <table
        className={classnames('mt-lg text-left table-auto w-fit', wide ? '[&>*]:text-[22px]' : '[&>*]:text-[14px]')}
      >
        <thead>
          <tr className="border-b border-black [&>th]:pr-3">
            <th>{translator('table.tax_breakdown.fields.tax_percentage')}</th>
            <th className="text-right">{translator('table.tax_breakdown.fields.product_type')}</th>
            <th className="text-right">{translator('table.tax_breakdown.fields.netto')}</th>
            <th className="text-right">{translator('table.tax_breakdown.fields.tax')}</th>
            <th className="text-right">{translator('table.tax_breakdown.fields.brutto')}</th>
          </tr>
        </thead>

        <tbody className="[&>tr>td]:pr-3">
          {payload.map((item, index) => (
            <tr key={index}>
              <td>{item.tax_percentage / 100}</td>
              <td className="text-right">
                {translator(`table.tax_breakdown.product_types.${item.product_type}`, {
                  percentage: item.tax_percentage / 100,
                })}
              </td>
              <td className="text-right">{formatCurrency(item.netto)}</td>
              <td className="text-right">{formatCurrency(item.tax)}</td>
              <td className="text-right">{formatCurrency(item.brutto)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
